/**
 * Hub 3D — overlay navigare; arbore din nav-tree.ts (aliniat cu sidebar).
 */

import { state, toast } from './core';
import { setRouteHash, syncHashToActiveSection } from './deep-link';
import type { HubNavNode } from './nav-tree';
import { getHubNavTreeForRole } from './nav-tree';

type StackFrame = { siblings: HubNavNode[]; groupLabel: string };
let _stack: StackFrame[] = [];
/** Rădăcină filtrată pe rol la deschiderea Hub (pentru Înapoi). */
let _hubRootNodes: HubNavNode[] = [];

const ORBIT_STEP_KEY = 10;
const ORBIT_STEP_BUTTON = 12;
const ORBIT_SWIPE = 28;
let _orbitDeg = 0;
let _touchStartX = 0;

function _applyOrbitTransform(): void {
    document.getElementById('snd-orbit-halo')?.style.setProperty('transform', `rotateY(${_orbitDeg}deg)`);
}

function _nudgeOrbit(deltaDeg: number): void {
    _orbitDeg += deltaDeg;
    _applyOrbitTransform();
}

/** Încearcă hub-3d-bg.webp / .jpg / .png în static/images/. */
function _tryLoadHubBackground(): void {
    const ambient = document.querySelector('#spatial-nav-demo .snd-ambient-bg') as HTMLElement | null;
    if (!ambient) return;
    ambient.classList.remove('snd-ambient-photo-loaded');
    ambient.style.removeProperty('--hub-bg-photo');
    const urls = ['/static/images/hub-3d-bg.webp', '/static/images/hub-3d-bg.jpg', '/static/images/hub-3d-bg.png'];
    let i = 0;
    const tryNext = (): void => {
        if (i >= urls.length) return;
        const url = urls[i++];
        const img = new Image();
        img.onload = () => {
            ambient.style.setProperty('--hub-bg-photo', `url('${url}')`);
            ambient.classList.add('snd-ambient-photo-loaded');
        };
        img.onerror = () => tryNext();
        img.src = url;
    };
    tryNext();
}

function _closeSidebarIfOpen(): void {
    document.getElementById('sidebar')?.classList.remove('open');
    document.getElementById('sidebar-backdrop')?.classList.remove('visible');
}

function _openSectionFromHub(section: string): void {
    const fn = (window as unknown as { showSection?: (id: string) => void }).showSection;
    if (typeof fn === 'function') {
        fn(`sec-${section}`);
    }
    closeSpatialNavDemo();
    _closeSidebarIfOpen();
}

function _openUsersFromHub(): void {
    // Delegate to Hub admin UI (Strategy A+B, CONTRACT v1.5 #28).
    // See _openHubAdminUsers() in auth-ui.ts for full rationale.
    const open = (window as unknown as { _openHubAdminUsers?: () => void })._openHubAdminUsers;
    if (typeof open === 'function') open();
    closeSpatialNavDemo();
    _closeSidebarIfOpen();
}

function _updateChrome(): void {
    const back = document.getElementById('snd-back');
    const title = document.getElementById('snd-title');
    if (!back || !title) return;
    if (_stack.length === 0) {
        back.hidden = true;
        title.textContent = 'Hub 3D';
    } else {
        back.hidden = false;
        title.textContent = _stack[_stack.length - 1].groupLabel;
    }
}

function _renderDeck(nodes: HubNavNode[]): void {
    const deck = document.getElementById('snd-deck');
    if (!deck) return;
    deck.innerHTML = '';
    deck.dataset.level = _stack.length === 0 ? '1' : '2';
    deck.classList.add('snd-deck--orbit');
    const n = Math.max(1, nodes.length);
    deck.style.setProperty('--snd-count', String(n));
    /* Rază mai mică = cardurile sunt mai aproape de „camera” → par mai mari pe ecran */
    deck.style.setProperty('--orbit-r', n > 5 ? 'min(210px, 28vw)' : 'min(240px, 32vw)');
    deck.style.setProperty('--spiral-pitch', _stack.length === 0 ? '-14px' : '-10px');

    nodes.forEach((node, i) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'snd-card';
        card.style.setProperty('--slot', String(i));
        if (node.kind === 'group') {
            card.innerHTML = `
                <span class="snd-card-icon" aria-hidden="true">${node.icon}</span>
                <span class="snd-card-body">
                    <span class="snd-card-label">${node.label}</span>
                    <span class="snd-card-sub">${node.subtitle || 'Submeniu'}</span>
                </span>
                <span class="snd-card-chev" aria-hidden="true">›</span>`;
            card.addEventListener('click', () => {
                _stack.push({ siblings: nodes, groupLabel: node.label });
                _renderDeck(node.children);
                _updateChrome();
            });
        } else if (node.kind === 'modal') {
            card.innerHTML = `
                <span class="snd-card-icon" aria-hidden="true">${node.icon}</span>
                <span class="snd-card-body">
                    <span class="snd-card-label">${node.label}</span>
                    <span class="snd-card-sub">${node.subtitle || 'Deschide dialogul'}</span>
                </span>`;
            card.addEventListener('click', () => {
                if (node.modal === 'users') _openUsersFromHub();
                else toast('Acțiune indisponibilă');
            });
        } else if (node.kind === 'link') {
            card.innerHTML = `
                <span class="snd-card-icon" aria-hidden="true">${node.icon}</span>
                <span class="snd-card-body">
                    <span class="snd-card-label">${node.label}</span>
                    <span class="snd-card-sub">${node.subtitle || 'Se deschide în tab nou'}</span>
                </span>`;
            card.addEventListener('click', () => {
                // sub hub.cpp-romania.com aplicația stă pe prefixul /maint
                const prefix = location.pathname.startsWith('/maint') ? '/maint' : '';
                window.open(prefix + node.href, '_blank');
            });
        } else {
            card.innerHTML = `
                <span class="snd-card-icon" aria-hidden="true">${node.icon}</span>
                <span class="snd-card-body">
                    <span class="snd-card-label">${node.label}</span>
                    <span class="snd-card-sub">${node.subtitle || 'Deschide secțiunea'}</span>
                </span>`;
            card.addEventListener('click', () => _openSectionFromHub(node.section));
        }
        deck.appendChild(card);
    });
}

function _goBack(): void {
    if (_stack.length === 0) return;
    _stack.pop();
    if (_stack.length === 0) {
        _renderDeck(_hubRootNodes);
    } else {
        const prev = _stack[_stack.length - 1];
        _renderDeck(prev.siblings);
    }
    _updateChrome();
}

export function openSpatialNavDemo(): void {
    const root = document.getElementById('spatial-nav-demo');
    if (!root) return;
    _stack = [];
    _orbitDeg = 0;
    _applyOrbitTransform();
    _tryLoadHubBackground();
    _hubRootNodes = getHubNavTreeForRole(state.currentUser?.role);
    if (_hubRootNodes.length === 0) {
        toast('Nu aveți destinații Hub pentru acest rol.', 'error');
        return;
    }
    document.body.classList.add('hub-3d-active');
    _renderDeck(_hubRootNodes);
    _updateChrome();
    root.classList.remove('hidden');
    root.setAttribute('aria-hidden', 'false');
    setRouteHash('hub');
    document.getElementById('snd-close')?.focus();
}

export function closeSpatialNavDemo(): void {
    const root = document.getElementById('spatial-nav-demo');
    if (!root) return;
    root.classList.add('hidden');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('hub-3d-active');
    syncHashToActiveSection();
    _stack = [];
}

export function initSpatialNavDemo(): void {
    const btn = document.getElementById('btn-spatial-demo');
    const root = document.getElementById('spatial-nav-demo');
    const perspective = document.getElementById('snd-perspective');
    if (!btn || !root || !perspective) return;

    btn.addEventListener('click', () => openSpatialNavDemo());

    document.getElementById('snd-close')?.addEventListener('click', () => closeSpatialNavDemo());
    document.getElementById('snd-backdrop')?.addEventListener('click', () => closeSpatialNavDemo());
    document.getElementById('snd-back')?.addEventListener('click', () => _goBack());

    document.getElementById('snd-orbit-prev')?.addEventListener('click', () => _nudgeOrbit(ORBIT_STEP_BUTTON));
    document.getElementById('snd-orbit-next')?.addEventListener('click', () => _nudgeOrbit(-ORBIT_STEP_BUTTON));

    const onWheel = (e: WheelEvent): void => {
        if (root.classList.contains('hidden')) return;
        e.preventDefault();
        const dy = e.deltaY + (e.shiftKey ? e.deltaX : 0);
        _nudgeOrbit(dy > 0 ? -2.2 : 2.2);
    };
    perspective.addEventListener('wheel', onWheel, { passive: false });

    perspective.addEventListener(
        'touchstart',
        (e: TouchEvent) => {
            if (e.touches.length === 1) _touchStartX = e.touches[0].clientX;
        },
        { passive: true }
    );
    perspective.addEventListener(
        'touchend',
        (e: TouchEvent) => {
            if (root.classList.contains('hidden') || !e.changedTouches.length) return;
            const dx = e.changedTouches[0].clientX - _touchStartX;
            if (dx > 48) _nudgeOrbit(ORBIT_SWIPE);
            else if (dx < -48) _nudgeOrbit(-ORBIT_SWIPE);
        },
        { passive: true }
    );

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (root.classList.contains('hidden')) return;
        if (e.key === 'Escape') {
            closeSpatialNavDemo();
            return;
        }
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            _nudgeOrbit(ORBIT_STEP_KEY);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            _nudgeOrbit(-ORBIT_STEP_KEY);
        }
    });
}
